import React from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Minus, Plus, Trash2, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useQuoteCart } from "@/context/QuoteCartContext";

export default function QuoteCartDrawer() {
  const { items, setQuantity, removeItem, open, setOpen, clear } = useQuoteCart();

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        data-testid="quote-cart-drawer"
        side="right"
        className="w-full sm:max-w-md bg-white border-brand-line p-0 flex flex-col"
      >
        <SheetHeader className="px-6 py-5 border-b border-brand-line">
          <SheetTitle className="font-display text-2xl text-brand-ink">Your Quote Cart</SheetTitle>
          <SheetDescription className="text-xs text-brand-stone font-ui">Add products, then request a personalized wholesale quote.</SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div data-testid="quote-cart-empty" className="text-center py-16">
              <div className="w-16 h-16 mx-auto rounded-full bg-brand-pinkLight grid place-items-center text-brand-red font-display text-2xl">∅</div>
              <p className="mt-4 text-sm text-brand-stone font-ui">Your quote cart is empty.</p>
              <Link to="/catalog" onClick={() => setOpen(false)} className="inline-block mt-6 text-sm font-semibold font-ui text-brand-red hover:underline" data-testid="cart-browse-catalog">
                Browse the collection →
              </Link>
            </div>
          ) : (
            <ul className="space-y-4">
              {items.map((i) => (
                <li
                  key={`${i.product_id}-${i.size_label}`}
                  data-testid={`cart-item-${i.product_id}-${i.size_label}`}
                  className="flex gap-3 items-center bg-brand-pinkSoft rounded-2xl border border-brand-line p-3"
                >
                  <img src={i.image_url} alt={i.product_name} className="w-16 h-16 object-contain rounded-xl bg-white" />
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-base text-brand-ink line-clamp-1">{i.product_name}</div>
                    <div className="text-xs text-brand-stone font-ui">{i.size_label} {i.size_label === '3"' ? "Mini" : "Whole"}</div>
                    <div className="mt-2 flex items-center gap-2">
                      <button
                        onClick={() => setQuantity(i.product_id, i.size_label, i.quantity - 1)}
                        className="w-7 h-7 grid place-items-center rounded-full border border-brand-line bg-white text-brand-ink hover:bg-brand-pinkLight"
                        data-testid={`cart-dec-${i.product_id}-${i.size_label}`}
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="text-sm font-semibold w-6 text-center font-ui">{i.quantity}</span>
                      <button
                        onClick={() => setQuantity(i.product_id, i.size_label, i.quantity + 1)}
                        className="w-7 h-7 grid place-items-center rounded-full border border-brand-line bg-white text-brand-ink hover:bg-brand-pinkLight"
                        data-testid={`cart-inc-${i.product_id}-${i.size_label}`}
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                  <button
                    onClick={() => removeItem(i.product_id, i.size_label)}
                    className="w-8 h-8 grid place-items-center rounded-full text-brand-stone hover:text-brand-red hover:bg-brand-pinkLight"
                    data-testid={`cart-remove-${i.product_id}-${i.size_label}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-brand-line px-6 py-4 space-y-3 bg-white">
            <Link
              to="/request-quote"
              onClick={() => setOpen(false)}
              data-testid="cart-checkout-quote"
              className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-full bg-brand-red text-white text-sm font-ui font-semibold hover:bg-brand-redDark transition"
            >
              Request Quote <ArrowRight className="w-4 h-4" />
            </Link>
            <button
              onClick={clear}
              data-testid="cart-clear"
              className="w-full text-xs text-brand-stone hover:text-brand-red font-ui"
            >
              Clear cart
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
